/**
 * Stale-bundle self-heal.
 *
 * Every website deploy publishes a fresh set of content-hashed chunks
 * (`/assets/MyDevEnvironmentDetail-<hash>.js`, `…css`) and removes the old
 * ones. A tab that was opened BEFORE the deploy still holds the old SPA shell
 * with the old manifest, so its next lazy route / CSS preload 404s:
 *
 *   Failed to fetch dynamically imported module …
 *   Unable to preload CSS for /assets/…css
 *   Refused to apply style … MIME type ('text/html')   (the 404 page)
 *
 * The standard remedy is a single hard reload onto the target route, which
 * fetches the new shell + manifest. These helpers are the pure, testable core:
 *
 *   - `isChunkLoadError(err)`               — is this one of those failures?
 *   - `shouldReloadForChunkError(now, storage)` — reload at most once per
 *     RELOAD_WINDOW_MS (guarded by a sessionStorage timestamp) so a genuinely
 *     broken deploy can't put the tab into a reload loop.
 *   - `reloadForChunkError(err, targetPath)` — the browser-side glue used by
 *     the router error hook and Vite's `vite:preloadError` event.
 *
 * No DOM access in the first two — they run under Node's test runner.
 */

export const CHUNK_RELOAD_KEY = 'cs.chunkReload'
export const RELOAD_WINDOW_MS = 60 * 1000

const CHUNK_ERROR_RE =
  /Failed to fetch dynamically imported module|Importing a module script failed|Unable to preload CSS|error loading dynamically imported module/i

/**
 * True when `err` (an Error, a string, or a `vite:preloadError`-style event
 * carrying `.payload`) describes a hashed-chunk load failure.
 */
export function isChunkLoadError(err) {
  if (!err) return false
  const candidate = err.payload && (err.payload.message || err.payload)
  const message =
    typeof err === 'string'
      ? err
      : (candidate && (typeof candidate === 'string' ? candidate : candidate.message)) ||
        err.message ||
        (err.reason && err.reason.message) ||
        ''
  return typeof message === 'string' && CHUNK_ERROR_RE.test(message)
}

/**
 * Decide whether a reload is allowed now, and — when it is — stamp `storage`
 * so the next call inside the window says no. `storage` is anything with
 * `getItem` / `setItem` (sessionStorage in the browser, a Map-like stub in
 * tests). Every storage access is wrapped: a throwing storage (Safari private
 * mode, blocked site data) falls back to allowing exactly the current reload
 * because we cannot remember having done one — better one reload than a
 * permanently broken tab.
 */
export function shouldReloadForChunkError(now = Date.now(), storage) {
  const store = storage === undefined ? safeSessionStorage() : storage
  if (!store) return true

  let last = 0
  try {
    const raw = store.getItem(CHUNK_RELOAD_KEY)
    last = raw ? Number(raw) : 0
    if (!Number.isFinite(last)) last = 0
  } catch (_) {
    last = 0
  }

  if (last && now - last < RELOAD_WINDOW_MS) return false

  try {
    store.setItem(CHUNK_RELOAD_KEY, String(now))
  } catch (_) {
    /* storage unavailable — still reload this once */
  }
  return true
}

function safeSessionStorage() {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null
  } catch (_) {
    return null
  }
}

/**
 * Browser-side glue: if `err` is a chunk-load failure and the once-per-window
 * guard allows it, hard-navigate to `targetPath` (or the current URL). Returns
 * true when a reload was issued so callers can skip their normal error
 * reporting for a failure we are about to heal.
 */
export function reloadForChunkError(err, targetPath) {
  if (!isChunkLoadError(err)) return false
  if (typeof window === 'undefined') return false
  if (!shouldReloadForChunkError(Date.now())) {
    console.warn('[chunk-reload] chunk-load error repeated within the reload window; not reloading again', err)
    return false
  }
  const target = targetPath || window.location.href
  console.warn('[chunk-reload] stale bundle detected after a deploy — reloading', target)
  window.location.assign(target)
  return true
}
